import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

const PLAN_PRESET_OPTIONS = [
  { value: '', label: 'Select plan' },
  { value: 'free', label: 'Free' },
  { value: 'basic', label: 'Basic' },
  { value: 'standard', label: 'Standard' },
  { value: 'premium', label: 'Premium' },
  { value: 'custom', label: 'Custom' }
];

const planNameFromPreset = (preset) => {
  switch (preset) {
    case 'free':
      return 'Free';
    case 'basic':
      return 'Basic';
    case 'standard':
      return 'Standard';
    case 'premium':
      return 'Premium';
    default:
      return '';
  }
};

const presetFromPlanName = (name) => {
  const n = String(name || '').trim();
  if (n === 'Free') return 'free';
  if (n === 'Basic' || n === 'Basic Plan') return 'basic';
  if (n === 'Standard' || n === 'Standard Plan') return 'standard';
  if (n === 'Premium' || n === 'Premium Plan') return 'premium';
  return 'custom';
};

const Subscription = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingSubscriptionId, setEditingSubscriptionId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const defaultSubscriptionForm = {
    durationMonths: '12',
    amount: '',
    currency: 'INR',
    discountPercentage: '0',
    playProductId:''
  };
  const [subscriptionForm, setSubscriptionForm] = useState(defaultSubscriptionForm);
  const [planPreset, setPlanPreset] = useState('');
  const [customPlanName, setCustomPlanName] = useState('');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

 useEffect(() => {
  fetchSubscription();
}, [currentPage]);

 const fetchSubscription = async () => {
  if (!API_URL) {
    toast.error('API URL not configured.');
    return;
  }
  setIsLoading(true);
  try {
    const response = await axios.post(
      API_URL,
      {
        action: 'getActiveSubscriptions',
        page: String(currentPage),
        limit: String(limit),
      },
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );

    const data = response.data;

    setRows(data.data || []);
    setTotalPages(data.totalPages || 1);
    setTotalCount(data.totalCount || 0);

  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.message ||
      'Failed to load subscription';
    toast.error(message);
  } finally {
    setIsLoading(false);
  }
};

  const openAddModal = () => {
    setModalMode('add');
    setEditingSubscriptionId(null);
    setPlanPreset('');
    setCustomPlanName('');
    setSubscriptionForm({ ...defaultSubscriptionForm });
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    const id = row?._id || row?.subscriptionId;
    if (!id) {
      toast.error('Subscription ID missing.');
      return;
    }
    setModalMode('edit');
    setEditingSubscriptionId(id);
    const pname = row.planName || row.plan || '';
    const preset = presetFromPlanName(pname);
    setPlanPreset(preset);
    setCustomPlanName(preset === 'custom' ? pname : '');
    setSubscriptionForm({
      durationMonths:
        row.durationMonths != null ? String(row.durationMonths) : '12',
      amount: row.amount != null ? String(row.amount) : '',
      currency: row.currency || 'INR',
      discountPercentage:
        row.discountPercentage != null
          ? String(row.discountPercentage)
          : '0',
        playProductId: row.playProductId || ''   

    });
    setIsModalOpen(true);
  };

  const closeAddModal = () => {
    setIsModalOpen(false);
    setModalMode('add');
    setEditingSubscriptionId(null);
    setPlanPreset('');
    setCustomPlanName('');
    setSubscriptionForm({ ...defaultSubscriptionForm });
  };

  const handlePageChange = (newPage) => {
  if (newPage >= 1 && newPage <= totalPages) {
    setCurrentPage(newPage);
  }
};

  const handlePlanPresetChange = (e) => {
    const v = e.target.value;
    setPlanPreset(v);
    if (v === 'free') {
      setCustomPlanName('');
      setSubscriptionForm((prev) => ({
        ...prev,
        durationMonths: '0',
        amount: '0',
        discountPercentage: '0',
        currency: 'INR',
        playProductId: ''
      }));
    } else if (v === 'custom') {
      setCustomPlanName('');
      setSubscriptionForm((prev) => ({
        ...prev,
        durationMonths:
          prev.durationMonths === '0' ? '12' : prev.durationMonths,
        amount: prev.amount === '0' ? '' : prev.amount,
        discountPercentage: prev.discountPercentage
      }));
    } else if (v) {
      setCustomPlanName('');
      setSubscriptionForm((prev) => ({
        ...prev,
        durationMonths:
          prev.durationMonths === '0' ? '12' : prev.durationMonths,
        amount: prev.amount === '0' ? '' : prev.amount,
        discountPercentage: prev.discountPercentage
      }));
    } else {
      setCustomPlanName('');
      setSubscriptionForm({ ...defaultSubscriptionForm });
    }
  };

  const isFreePlan = planPreset === 'free';
  const isCustomPlan = planPreset === 'custom';

  const handleSubscriptionFieldChange = (field, value) => {
    setSubscriptionForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSubscription = async () => {
    const userId = localStorage.getItem('userid');
    if (!userId) {
      toast.error('User not found. Please login again.');
      return;
    }
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }
    if (!planPreset) {
      toast.error('Please select a plan.');
      return;
    }
    let planName = '';
    if (planPreset === 'custom') {
      planName = (customPlanName || '').trim();
      if (!planName) {
        toast.error('Please enter a custom plan name.');
        return;
      }
    } else {
      planName = planNameFromPreset(planPreset);
      if (!planName) {
        toast.error('Invalid plan selection.');
        return;
      }
    }
    const playProductId = (subscriptionForm.playProductId || '').trim();

if (!isFreePlan && !playProductId) {
  toast.error('Product ID is required for paid plans.');
  return;
}

    const durationMonths = parseInt(String(subscriptionForm.durationMonths), 10);
    const amount = Number(subscriptionForm.amount);
    const discountPercentage = Number(subscriptionForm.discountPercentage);

    if (isFreePlan) {
      if (durationMonths !== 0) {
        toast.error('Free plan duration must be 0.');
        return;
      }
      if (amount !== 0) {
        toast.error('Free plan amount must be 0.');
        return;
      }
      if (discountPercentage !== 0) {
        toast.error('Free plan discount must be 0.');
        return;
      }
    } else {
      if (!Number.isFinite(durationMonths) || durationMonths < 1) {
        toast.error('Duration (months) must be at least 1.');
        return;
      }
      if (!Number.isFinite(amount) || amount < 0) {
        toast.error('Amount must be a valid number (0 or greater).');
        return;
      }
    }
    if (
      !Number.isFinite(discountPercentage) ||
      discountPercentage < 0 ||
      discountPercentage > 100
    ) {
      toast.error('Discount must be between 0 and 100.');
      return;
    }
    const currency = (subscriptionForm.currency || 'INR').trim() || 'INR';

    setIsSaving(true);
    try {
      const isEdit = modalMode === 'edit' && editingSubscriptionId;
      const payload = isEdit
        ? {
            action: 'updateSubscription',
            subscriptionId: editingSubscriptionId,
            planName,
            durationMonths,
            amount,
            currency,
            discountPercentage,
            userId,
            playProductId
          }
        : {
            action: 'createSubscription',
            planName,
            durationMonths,
            amount,
            currency,
            discountPercentage,
            userId,
            playProductId
          };

      const response = await axios.post(API_URL, payload, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success(
        response.data?.message ||
          (isEdit ? 'Subscription updated' : 'Subscription created')
      );
      closeAddModal();
      await fetchSubscription();
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        (modalMode === 'edit'
          ? 'Failed to update subscription'
          : 'Failed to create subscription');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteModal = (row) => {
    setSelectedRow(row);
    setIsDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteOpen(false);
    setSelectedRow(null);
  };

  const handleDeleteSubscription = async () => {
    if (!API_URL) {
      toast.error('API URL not configured.');
      return;
    }
    const subscriptionId = selectedRow?._id || selectedRow?.subscriptionId;
    if (!subscriptionId) {
      toast.error('Subscription ID missing.');
      return;
    }
    setIsDeleting(true);
    try {
      const response = await axios.post(API_URL, {
        action: 'deleteSubscription',
        subscriptionId
      }, {
        headers: { 'Content-Type': 'application/json' }
      });
      toast.success(response.data?.message || 'Subscription deleted');
      closeDeleteModal();
      await fetchSubscription();
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to delete subscription';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const getPlanLabel = (row) =>
    row.plan || row.planName || row.subscriptionPlan || row.name || '-';

  const getModifiedBy = (row) => {
    const by = row.updatedBy || row.createdBy || row.lastModifiedBy || row.modifiedBy;
    if (!by) return '-';
    if (typeof by === 'string') return by;
    if (typeof by === 'object') return by.name || by.email || by._id || '-';
    return String(by);
  };

  const getModifiedAt = (row) =>
    row.updatedAt || row.createdAt || row.lastModifiedAt || row.modifiedAt || '';

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).replace(',', ' ');
  };

  const formatMoney = (value, currency = 'INR') => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '-';
    const formatted = n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${currency} ${formatted}`;
  };

  const getDiscountLabel = (row) => {
    const pct = row.discountPercentage;
    const amt = row.discountAmount;
    const p =
      pct != null && Number.isFinite(Number(pct))
        ? `${Number(pct)}%`
        : '—';
    const numAmt = Number(amt);
    if (Number.isFinite(numAmt) && numAmt > 0) {
      return `${p} (${formatMoney(amt, row.currency || 'INR')})`;
    }
    return p;
  };

  const renderPagination = () => {
  const pages = [];
  const maxVisiblePages = 5;

  let startPage = Math.max(
    1,
    currentPage - Math.floor(maxVisiblePages / 2)
  );

  let endPage = Math.min(
    totalPages,
    startPage + maxVisiblePages - 1
  );

  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="pagination-wrapper">

      <div className="pagination-info">
        Showing {rows.length > 0 ? (currentPage - 1) * limit + 1 : 0}
        {" "}to{" "}
        {Math.min(currentPage * limit, totalCount)}
        {" "}of {totalCount} entries
      </div>


      <div className="pagination-controls">

        <button
          className="pagination-btn"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
        >
          <i className="fas fa-angle-double-left"></i>
        </button>


        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <i className="fas fa-angle-left"></i>
        </button>


        {pages.map((page) => (
          <button
            key={page}
            className={`pagination-btn ${
              currentPage === page ? 'active' : ''
            }`}
            onClick={() => handlePageChange(page)}
          >
            {page}
          </button>
        ))}


        <button
          className="pagination-btn"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <i className="fas fa-angle-right"></i>
        </button>


        <button
          className="pagination-btn"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <i className="fas fa-angle-double-right"></i>
        </button>

      </div>

    </div>
  );
};

  return (
    <div className="employee-form">
      <div className="content-header subscription-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Subscription</h1>
            <p>View subscription details.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={openAddModal}
            >
              Add Subscription
            </button>
          </div>
        </div>
      </div>


      <div className="card employee-content-card">
        <div className="card-body">
          {isLoading ? (
            <div className="employee-loading">
              <LoadingIndicator size={120} />
            </div>
          ) : rows.length === 0 ? (
            <div className="employee-empty">No subscription data found.</div>
          ) : (
            <div className="employee-table-wrapper">
              <table className="table table-borderless employee-table subscription-table">
                <thead>
                  <tr>
                    <th>Sl No.</th>
                    <th>Plan name</th>
                    <th>Duration</th>
                    <th>Amount</th>
                    <th>Discount</th>
                    <th>Total amount</th>
                    <th>Last modified</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.id || row.subscriptionId || row._id || idx}>
                      <td>{(currentPage - 1) * limit + idx + 1}</td>
                      <td>{getPlanLabel(row)}</td>
                      <td>
                        {row.durationMonths != null
                          ? `${row.durationMonths} mo`
                          : '-'}
                      </td>
                      <td>{formatMoney(row.amount, row.currency)}</td>
                      <td>{getDiscountLabel(row)}</td>
                      <td>
                        {formatMoney(
                          row.totalAmount != null ? row.totalAmount : row.amount,
                          row.currency
                        )}
                      </td>
                      <td>
                        <div style={{ lineHeight: 1.35 }}>
                          <div>{getModifiedBy(row)}</div>
                          <div
                            style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              marginTop: '2px'
                            }}
                          >
                            {formatDateTime(getModifiedAt(row))}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="employee-actions">
                          <button
                            className="action-icon-btn view"
                            type="button"
                            title="View"
                            onClick={() => navigate(`/subscription/${row._id || row.subscriptionId}`)}
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          <button
                            className="action-icon-btn edit"
                            type="button"
                            title="Edit"
                            onClick={() => openEditModal(row)}
                          >
                            <i className="fa-solid fa-pen"></i>
                          </button>
                          <button
                            className="action-icon-btn delete"
                            type="button"
                            title="Delete"
                            onClick={() => openDeleteModal(row)}
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {renderPagination()}
            </div>
          )}
        </div>
      </div>

      {isModalOpen && (
        <div className="crop-overlay">
          <div className="crop-modal">
            <div className="crop-header">
              <h5>
                {modalMode === 'edit' ? 'Edit Subscription' : 'Add Subscription'}
              </h5>
              <button type="button" className="crop-close" onClick={closeAddModal}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <div className="form-group">
                <label className="profile-label" htmlFor="subscription-plan-preset">
                  Plan <span className="required-asterisk">*</span>
                </label>
                <select
                  id="subscription-plan-preset"
                  className="form-control"
                  value={planPreset}
                  onChange={handlePlanPresetChange}
                >
                  {PLAN_PRESET_OPTIONS.map((opt) => (
                    <option key={opt.value || '_empty'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label
                  className="profile-label"
                  htmlFor="subscription-productId"
                >
                  Product ID{" "}
                  {!isFreePlan && <span className="required-asterisk">*</span>}
                </label>
                <input
                  id="subscription-productId"
                  type="text"
                  className="form-control"
                  readOnly={isFreePlan}
                  style={
                    isFreePlan
                      ? { backgroundColor: "#f8f9fa", cursor: "default" }
                      : undefined
                  }
                  value={subscriptionForm.playProductId || ""}
                  onChange={(e) =>
                    handleSubscriptionFieldChange(
                      "playProductId",
                      e.target.value,
                    )
                  }
                />
                {isFreePlan && (
                  <small className="text-muted d-block mt-1">
                    Product ID not required for Free plan.
                  </small>
                )}
              </div>
              {isCustomPlan && (
                <div className="form-group">
                  <label className="profile-label" htmlFor="subscription-customPlanName">
                    Custom plan name <span className="required-asterisk">*</span>
                  </label>
                  <input
                    id="subscription-customPlanName"
                    type="text"
                    className="form-control"
                    placeholder="Enter plan name"
                    value={customPlanName}
                    onChange={(e) => setCustomPlanName(e.target.value)}
                  />
                </div>
              )}
              <div className="form-group">
                <label className="profile-label" htmlFor="subscription-durationMonths">
                  Duration (months){' '}
                  {!isFreePlan && <span className="required-asterisk">*</span>}
                </label>
                <input
                  id="subscription-durationMonths"
                  type="number"
                  className="form-control"
                  min={isFreePlan ? 0 : 1}
                  step={1}
                  readOnly={isFreePlan}
                  style={
                    isFreePlan
                      ? { backgroundColor: '#f8f9fa', cursor: 'default' }
                      : undefined
                  }
                  value={subscriptionForm.durationMonths}
                  onChange={(e) =>
                    handleSubscriptionFieldChange('durationMonths', e.target.value)
                  }
                />
                {isFreePlan && (
                  <small className="text-muted d-block mt-1">
                    Free plan is fixed at 0 months.
                  </small>
                )}
              </div>
              <div className="form-group">
                <label className="profile-label" htmlFor="subscription-amount">
                  Amount {!isFreePlan && <span className="required-asterisk">*</span>}
                </label>
                <input
                  id="subscription-amount"
                  type="number"
                  className="form-control"
                  min={0}
                  step={0.01}
                  readOnly={isFreePlan}
                  style={
                    isFreePlan
                      ? { backgroundColor: '#f8f9fa', cursor: 'default' }
                      : undefined
                  }
                  placeholder={isFreePlan ? '0' : 'e.g. 500'}
                  value={subscriptionForm.amount}
                  onChange={(e) =>
                    handleSubscriptionFieldChange(
                      'amount',
                      e.target.value === '' ? '' : e.target.value
                    )
                  }
                />
              </div>
              <div className="form-group">
                <label className="profile-label" htmlFor="subscription-currency">
                  Currency
                </label>
                <input
                  id="subscription-currency"
                  type="text"
                  className="form-control"
                  placeholder="INR"
                  readOnly={isFreePlan}
                  style={
                    isFreePlan
                      ? { backgroundColor: '#f8f9fa', cursor: 'default' }
                      : undefined
                  }
                  value={subscriptionForm.currency}
                  onChange={(e) => handleSubscriptionFieldChange('currency', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="profile-label" htmlFor="subscription-discount">
                  Discount (%)
                </label>
                <input
                  id="subscription-discount"
                  type="number"
                  className="form-control"
                  min={0}
                  max={100}
                  step={0.1}
                  readOnly={isFreePlan}
                  style={
                    isFreePlan
                      ? { backgroundColor: '#f8f9fa', cursor: 'default' }
                      : undefined
                  }
                  value={subscriptionForm.discountPercentage}
                  onChange={(e) =>
                    handleSubscriptionFieldChange('discountPercentage', e.target.value)
                  }
                />
                {isFreePlan && (
                  <small className="text-muted d-block mt-1">
                    No discount on Free plan.
                  </small>
                )}
              </div>
            </div>
            <div className="crop-footer">
              <button type="button" className="btn btn-secondary" onClick={closeAddModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveSubscription}
                disabled={isSaving}
              >
                {isSaving ? (
                  <LoadingIndicator size={22} className="inline-loader" />
                ) : modalMode === 'edit' ? (
                  'Update'
                ) : (
                  'Add'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteOpen && (
        <div className="crop-overlay">
          <div className="crop-modal confirm-modal">
            <div className="crop-header">
              <h5>Delete Subscription</h5>
              <button type="button" className="crop-close" onClick={closeDeleteModal}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>
            <div className="crop-body">
              <p className="confirm-message">
                Are you sure want to delete the {getPlanLabel(selectedRow)} Subscription plan?
              </p>
            </div>
            <div className="crop-footer">
              
              <button
                type="button"
                className="btn confirm-delete-btn"
                onClick={handleDeleteSubscription}
                disabled={isDeleting}
              >
                {isDeleting ? <LoadingIndicator size={22} className="inline-loader" /> : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;